import { Avatar } from '@material-ui/core';
import { useCallback, useEffect, useState } from 'react';

type LogoProps = {
  id: number;
  name: string;
  size?: number;
};

const fallbackColors = [
  '#F44336',
  '#E91E63',
  '#9C27B0',
  '#3F51B5',
  '#03A9F4',
  '#009688',
  '#4CAF50',
  '#FF9800',
  '#795548',
  '#607D8B'
];

const getColorFromId = (id: string) => {
  let hash = 0;
  for (let i = 0; i < id.length; i++) {
    // eslint-disable-next-line no-bitwise
    hash = id.charCodeAt(i) + ((hash << 5) - hash);
  }
  const index = Math.abs(hash) % fallbackColors.length;
  return fallbackColors[index];
};

function stringAvatar(name: string) {
  if (!name || typeof name !== 'string') return { children: '?' };

  const parts = name.trim().split(/\s+/);
  const initials = parts
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0].toUpperCase())
    .join('');

  return {
    children: initials || '?'
  };
}

export const CompanyLogo: React.FC<LogoProps> = ({ id, name, size = 64 }) => {
  const [src, setSrc] = useState<string | null>(null);

  const fallbackColor = getColorFromId(id.toString());

  const tryLoadImage = async (baseName: string) => {
    const formats = ['svg', 'png'];
    for (const ext of formats) {
      const url = `/static/edi_partners/${baseName}.${ext}`;
      const img = new Image();
      img.src = url;
      // eslint-disable-next-line no-await-in-loop
      const success = await new Promise<boolean>((resolve) => {
        img.onload = () => resolve(true);
        img.onerror = () => resolve(false);
      });

      if (success) {
        return url;
      }
    }
    return null;
  };

  useEffect(() => {
    const loadImage = async () => {
      const foundSrc = await tryLoadImage(`${id}_mainlogo`);
      setSrc(foundSrc);
    };
    loadImage();
  }, [id]);

  const handleError = useCallback(() => {
    setSrc(null);
  }, [id]);

  const commonProps = {
    alt: name,
    src: src ?? undefined,
    onError: handleError,
    sx: {
      width: size,
      height: size
    }
  };

  return src ? (
    <Avatar
      {...commonProps}
      variant="square"
      sx={{
        ...commonProps.sx,
        bgcolor: 'transparent',
        '& img': {
          objectFit: 'contain'
        }
      }}
    />
  ) : (
    <Avatar
      {...commonProps}
      {...stringAvatar(name)}
      variant="circular"
      sx={{
        ...commonProps.sx,
        color: 'white',
        bgcolor: fallbackColor
      }}
    />
  );
};
